'use client';

import { useMemo, useRef } from 'react';

import {
  ActionBarWrapper,
  Button,
  Card,
  Label,
  Pagination,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/ui';
import { IconNewsOff, IconX } from '@tabler/icons-react';
import { APIImageType, Place } from '@types';
import {
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  useInstantSearch,
  usePagination,
  useRefinementList,
} from 'react-instantsearch-core';

import { replaceDynamicData } from '@/lib/server';

import { BlockBase } from '../utils';
import { Hit } from './hit';
import { Skeleton } from './skeleton';

export interface SearchPost {
  brandPosts: string[];
  categories: string[];
  excerpt: string;
  id: string;
  image: APIImageType[];
  preserveAspectRatio: boolean;
  publishedDate: number;
  slug: string;
  title: string;
  venuePosts: string[];
}

export function PostGridClient({ id, params: { venue }, place }: BlockBase & { place: Place | undefined }) {
  const sectionRef = useRef<HTMLDivElement>(null);

  const { items } = useHits<SearchPost>();
  const transformedItems = useMemo(() => {
    return replaceDynamicData({ items, ...place }).items;
  }, [items, place]);

  const { status, setIndexUiState } = useInstantSearch();
  const setCategory = (value: string) => {
    setIndexUiState((prev) => ({
      ...prev,
      refinementList: {
        ...prev.refinementList,
        categories: [value],
      },
    }));
  };

  // Pagination
  const { refine: setPage, currentRefinement: page, nbPages } = usePagination();

  // Categories
  const { items: categories } = useRefinementList({
    attribute: 'categories',
  });
  const { items: filters } = useCurrentRefinements();
  const { refine: clearCategory } = useClearRefinements({
    includedAttributes: ['categories'],
  });
  const category = filters.find((f) => f.attribute === 'categories')?.refinements?.[0]?.value;

  const selectStyles = 'h-14 min-w-max items-center justify-between gap-3 border-none bg-transparent';

  return (
    <>
      <ActionBarWrapper className="-mt-16 w-[calc(100%-2rem)] overflow-clip sm:-mt-28 lg:w-max">
        <Select value={String(category || '')} onValueChange={setCategory}>
          <div className="group relative flex w-full items-center gap-1 lg:min-w-96">
            <Label className="sr-only">Category</Label>
            <Select value={String(category || '')} onValueChange={setCategory}>
              <SelectTrigger className={selectStyles}>
                <SelectValue placeholder="All categories" />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Categories</SelectLabel>
                  <SelectSeparator />
                  {categories.map(({ value, count }) => (
                    <SelectItem className="group" key={value} value={value}>
                      <span className="mr-8 w-full">{value}</span>
                      <span className="text-muted-foreground group-hover:text-accent-foreground group-focus:text-accent-foreground">
                        {count}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {category && (
              <Button
                variant="ghost"
                size="icon"
                className="bg-background absolute right-2 rounded-sm"
                onClick={() => clearCategory()}
              >
                <IconX className="size-4" />
              </Button>
            )}
          </div>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Categories</SelectLabel>
              {categories.map(({ value, count }) => (
                <SelectItem key={value} value={value}>
                  {value} ({count})
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </ActionBarWrapper>

      <section
        ref={sectionRef}
        id={id}
        className="grid w-full max-w-(--breakpoint-2xl) grid-cols-1 items-start gap-4 gap-y-8 px-4 sm:grid-cols-2 sm:gap-8 sm:gap-y-12 sm:px-8 lg:grid-cols-3"
      >
        {!status || status === 'loading' ? (
          Array.from({ length: 36 }).map((_, i) => <Skeleton key={i} />)
        ) : !items?.length && status === 'idle' ? (
          <Card
            icon={{
              svg: <IconNewsOff stroke={1.5} size={64} />,
            }}
            contentAlignment={{
              alignment: { horizontalAlignment: 'center' },
            }}
            className="col-span-full"
            layout="dialog"
            title="No posts available."
            description="Please try again later."
          />
        ) : (
          transformedItems.map((hit, index) => <Hit key={`post-grid-${index}`} {...hit} venue={venue} />)
        )}
      </section>

      {nbPages > 1 && (
        <Pagination
          variant="numbers"
          totalPages={nbPages}
          scrollTargetRef={sectionRef}
          // ... how does this work?
          currentPage={page + 1 || 1}
          onPageChange={(page) => setPage(page - 1)}
        />
      )}
    </>
  );
}
